<script lang="ts">
import { useRewards, useRewardsClient, useEventListener, useDevice, trackGameOpen, useGameSection, useGameSections, useNimbusConfig } from '#imports'
import { isEmpty } from 'es-toolkit/compat'
import type { LayoutPrefetch } from '@core/types'

// The setup's first data round for `to`, so the layout's own calls take these requests over.
export const prefetch: LayoutPrefetch = (to, { layoutMounts }) => {
    const heroPage = to.meta.heroPage as string | undefined

    useGameSections({
        lazy: false,
        enabled: !!to.meta.enablePersonalizedGameHero,
        pageSlug: to.meta.pageSlug as string | undefined,
    })
    usePageData(to.meta.contentPage as string | undefined)

    if (!layoutMounts) return

    const { isMobile } = useDevice()
    const disableOnMobile = !!to.meta.disableHeroOnMobile
    const campaignHeroSlug = to.meta.enableCampaignHero ? parseCampaignHeroSlug(to.query.kampagne) : undefined
    const requestHeroData = !!heroPage && !(disableOnMobile && isMobile)

    usePaymentsClient().useGetDepositSuccessfulCount({ server: true, lazy: false })
    useHero({ page: campaignHeroSlug ?? heroPage, disableOnMobile, enabled: () => requestHeroData })
    useHeroGames({ enabled: () => !!heroPage })
    useGlobalNotifications()
}
</script>

<script setup lang="ts">
const props = defineProps<{
    enableLobbyHero?: boolean
}>()

const route = useRoute()

const { useGetDepositSuccessfulCount } = usePaymentsClient()
const { isLoggedIn, data: user } = useUserInfo()
const { data: accountDetails } = useSeamlessWalletAccountDetails()
const { isMobile } = useDevice()
const { mode: colorSchemeMode } = useColorScheme()

// Layout Props
const layoutProps = computed(() => {
    return {
        bodyPadding: route.meta.layoutBodyPadding ?? true,
        backgroundColor: route.meta.layoutBackgroundColor
    }
})

// Hero
const { imagePreset } = useHeroConfig()

const showLobbyHero = computed(() => !!props.enableLobbyHero)

const campaignHeroSlug = computed(() =>
    route.meta?.enableCampaignHero ? parseCampaignHeroSlug(route.query.kampagne) : undefined
)

const heroDisabledOnMobile = computed(() => !!route.meta?.disableHeroOnMobile && isMobile)

const requestHeroData = computed(() => {
    if (heroDisabledOnMobile.value) return false
    if (!route.meta?.heroPage) return false

    return true
})

const fetchPersonalizedContent = computed(() => !!route.meta?.enablePersonalizedGameHero)

const depositCountQuery = useGetDepositSuccessfulCount({ server: true, lazy: false })
const heroQuery = useHero({
    page: campaignHeroSlug.value ?? (route.meta?.heroPage as string | undefined),
    disableOnMobile: !!route.meta?.disableHeroOnMobile,
    enabled: () => requestHeroData.value
})
const gameSectionsQuery = useGameSections({
    lazy: false,
    enabled: fetchPersonalizedContent,
    pageSlug: () => route.meta?.pageSlug as string | undefined,
})

// Started here, not in the hero components or the page, so they join a request that is
// already in flight instead of adding rounds after the layout's own data has resolved.
const heroGamesQuery = useHeroGames({ enabled: () => !!route.meta.heroPage })
const globalNotificationsQuery = useGlobalNotifications()
const pageDataQuery = usePageData(route.meta.contentPage as string | undefined)
const layoutQueries = Promise.all([depositCountQuery, heroQuery, heroGamesQuery, globalNotificationsQuery, pageDataQuery])

// The first section's games depend on the section list alone, so they start as soon as
// it resolves instead of after every query above.
await gameSectionsQuery

// Track the first section slug reactively: this layout is persistent across
// SPA navigations, so a const captured at setup is undefined forever when the
// layout was first mounted on a route where personalization was disabled.
const firstSectionSlug = computed(() => gameSectionsQuery.data.value?.[0]?.slug ?? '')
const personalizedGamesSectionQuery = useGameSection({
    slug: firstSectionSlug,
    lazy: false,
    enabled: computed(() => fetchPersonalizedContent.value && !!firstSectionSlug.value),
})

const [{ isFirstDeposit }, hero] = await layoutQueries

// SSR fallback: unknown campaign slugs produce an empty response — recover to the default hero.
// Guard mirrors `hero.enabled` so we don't issue a fallback when no hero was fetched.
if (campaignHeroSlug.value
    && requestHeroData.value
    && isEmpty(hero.data.value)
    && route.meta?.heroPage
    && route.meta.heroPage !== campaignHeroSlug.value) {
    await hero.change({ page: route.meta.heroPage as string })
}

// Hero Rotator. Owns the hero slot on every hero page for any brand that configures
// nimbus.components.HeroRotator — a no-op for every other brand, which falls through to
// the static/personalized hero.
const { components: nimbusComponents } = useNimbusConfig()
const showHeroRotator = computed(() =>
    !!route.meta?.heroPage
    && !!nimbusComponents?.HeroRotator
)

// True when the hero service returns an active campaign (scheduled campaign flag, or an
// explicit ?kampagne= deep-link). On rotator brands it renders as the full hero above the
// rotator; the rotator itself is games-only.
const showCampaignHero = computed(() =>
    !isEmpty(hero.data.value)
    && (!!hero.data.value?.campaign || !!campaignHeroSlug.value)
)

// The campaign hero stacked above the rotator already features its game — keep the
// rotator from picking it too, so the game isn't shown twice.
const rotatorExcludeSlug = computed(() =>
    showCampaignHero.value ? hero.data.value?.game_slug : undefined
)

// True only when personalized sections are actually visible (loading or non-empty),
// vs `fetchPersonalizedContent` which only reflects configuration.
const personalizedContentVisible = computed(() =>
    fetchPersonalizedContent.value
    && (gameSectionsQuery.isLoading.value || (gameSectionsQuery.data.value?.length ?? 0) > 0)
)

await personalizedGamesSectionQuery

const heroDisplayMode = computed<'service' | null>(() => {
    if (!requestHeroData.value) return null
    return !isEmpty(hero.data.value) ? 'service' : null
})

const showHero = computed(() => {
    if (!route.meta?.heroPage) return false
    if (heroDisabledOnMobile.value) return false

    // Rotator owns the slot regardless of service/personalized hero data.
    if (showHeroRotator.value) return true

    return requestHeroData.value
})

const showStickyTerms = computed(() => {
    if (!heroDisplayMode.value) return false
    if (!showHero.value) return false
    if (heroDisabledOnMobile.value) return false

    // On rotator brands the only terms-bearing hero is the campaign hero on top.
    if (showHeroRotator.value && !showCampaignHero.value) return false

    if (isLoggedIn.value) {
        if (!route.meta?.termsShown) return false
        return !!(hero.data.value?.short_terms || hero.data.value?.long_terms)
    }

    return !!route.meta?.termsShown
})

// Promotions
const { useGetReminders, useGetPendingReward } = useRewards()

const pendingReward = useGetPendingReward()
// Independent of each other: one server round for both instead of two in sequence.
const reminderQuery = useGetReminders({
    enabled: () => pendingReward.status.value === 'success' && !pendingReward.data.value && !route.query.txId
})
const reclaimableReminderQuery = usePromotionReclaimableReminder()
await Promise.all([reminderQuery, reclaimableReminderQuery])

const isFirstBrandLoginWithBalance = computed(() =>
    user.value?.nthLoginForBrand === 1 && Number(accountDetails.value?.balance) >= 1
)

const isSoftSignedUpWithBalance = computed(() =>
    user.value?.wasSoftSignedUp && Number(accountDetails.value?.balance) >= 1
)

const isEligibleForAccountCompletion = computed(() =>
    !!(
        isLoggedIn.value
        && user.value?.isValidated
        && isFirstDeposit.value
        && !isFirstBrandLoginWithBalance.value
        && !isSoftSignedUpWithBalance.value
    )
)

if (isLoggedIn.value && import.meta.client) {
    // Remove promotion if the game with freespins was started from a tile
    useEventListener(document, 'gameStarted', (payload) => {
        const slug = (payload as CustomEvent<string>).detail
        const reminder = reminderQuery.data.value?.find(r => r.bonus?.games.some(game => game.slug === slug))

        if (reminder) {
            reminderQuery.close(reminder)
        }
    })
}

const { data: reclaimableReminder } = reclaimableReminderQuery

const { notice: previousLoginNotice } = usePreviousLoginNotice()

const { useGetPendingOffersWithPaymentRequirements } = useRewardsClient()

const signupBonusOffers = useGetPendingOffersWithPaymentRequirements({
    enabled: isEligibleForAccountCompletion,
    hasBonus: offer => offer.signup?.toggleable === false,
})

const hasSignupBonus = signupBonusOffers.hasSignupBonus

// Reserves via SSR-resolvable signals only; the client-only balance
// checks can retract it after paint, which collapses (not pushes) the layout.
const reserveAccountCompletion = computed(() =>
    !route.meta.hidePromotions
    && isEligibleForAccountCompletion.value
)

if (import.meta.client) {
    watch(() => route.path, () => {
        // The destination route can update before this layout unmounts.
        if ((route.meta?.layout ?? 'default') !== 'default') return

        if (isLoggedIn.value) {
            pendingReward.refresh()
        }
        if (isEligibleForAccountCompletion.value) {
            signupBonusOffers.refresh()
        }
    })
}

provide(LAYOUT_CONTEXT_KEY, {
    hero,
    personalizedGamesSection: personalizedGamesSectionQuery.data,
    personalizedGamesSectionLoading: personalizedGamesSectionQuery.isLoading,
    fetchPersonalizedContent,
    personalizedContentVisible,
})
</script>

<template>
    <div class="default">
        <UILayout v-bind="layoutProps" :reserve-account-completion="reserveAccountCompletion">
            <template #header>
                <SelfAssessmentContainer />
                <NavigationContainer />
            </template>

            <template v-if="!route.meta.hidePromotions && hasSignupBonus !== undefined" #accountCompletion>
                <LazyIdleNimbusAccountCompletion :show-bonus="hasSignupBonus" hydrate-on-idle />
            </template>

            <template #promotions>
                <Notifications />
                <GlobalNotifications placement="top_banner" />
                <LazyIdleFlashMessages v-if="previousLoginNotice" :notice="previousLoginNotice" hydrate-on-idle />

                <template v-if="!route.meta.hidePromotions">
                    <LazyIdleNimbusNotificationFlashContainer hydrate-on-idle />
                    <LazyIdlePromotionNextCounter
                        v-if="reclaimableReminder"
                        :reclaimable-reminder="reclaimableReminder"
                        hydrate-on-idle
                    />
                    <LazyIdlePromotionContainer hydrate-on-idle />
                </template>
            </template>

            <template v-if="showLobbyHero" #lobbyHero>
                <NimbusLobbyHero />
            </template>

            <template v-if="showHero" #hero>
                <div v-if="showHeroRotator">
                    <div v-if="showCampaignHero">
                        <NimbusHeroContainer
                            :data="hero.data.value"
                            :preset="imagePreset"
                            @game-click="game => trackGameOpen('hero', game)"
                        />
                        <StickyTerms
                            v-if="showStickyTerms"
                            class="default__hero-terms"
                            :hero-data="hero.data.value"
                            reset-margin
                            background-color="var(--background, #e9eaee)"
                        />
                    </div>
                    <div>
                        <NimbusHeroRotatorContainer
                            :class="{ 'default__rotator--stacked': showCampaignHero }"
                            :personalized="fetchPersonalizedContent"
                            :exclude-slug="rotatorExcludeSlug"
                        />
                    </div>
                </div>
                <NimbusHeroContainer
                    v-else-if="heroDisplayMode === 'service'"
                    :data="hero.data.value"
                    :preset="imagePreset"
                    @game-click="game => trackGameOpen('hero', game)"
                />
            </template>

            <!-- Rotator brands render terms inside the #hero slot, under the campaign hero (see above). -->
            <template v-if="showStickyTerms && !showHeroRotator" #terms>
                <StickyTerms
                    :hero-data="hero.data.value"
                    reset-margin
                    :sticky-config="{ container: '.layout__container', minHeight: 48, offset: 50, backgroundColor: 'var(--background, #e9eaee)' }"
                />
            </template>

            <template v-if="route.meta?.sidebar" #aside>
                <LazySidebarContainer />
            </template>

            <template #main>
                <slot />
            </template>

            <template #footer>
                <LazyVisibleFooterContainer :key="colorSchemeMode" hydrate-on-visible />
            </template>
        </UILayout>
    </div>
</template>

<style lang="scss" scoped>
// Gap between the campaign hero and its static terms band. The sticky variant gets this
// spacing from `.offer-terms--sticky` (margin/padding-top); the static band under the
// campaign hero has no sticky-config, so it needs its own top padding to not sit flush
// against the hero. Padding (not margin) keeps the band's background continuous.
.default__hero-terms {
    padding-top: 16px;

    @media (min-width: $screen-md) {
        padding-top: 24px;
    }
}

// Gap between the campaign hero (+ its terms) and the games rotator stacked below it.
.default__rotator--stacked {
    margin-top: 16px;

    @media (min-width: $screen-md) {
        margin-top: 24px;
    }
}
</style>
